import { IProduct } from '@core/product/models/product.model';
import {
  ECapacityBookingRestriction,
  ICapacityOrderItem,
  ICapacityPackageItem,
  ICapacityProductResource,
  ICapacityProductSessionResourceTime
} from '@roller/product-capacity-service';
import { IOrderItem } from '@core/order/models/order-item.model';
import { IProductInfo } from '@core/product/models/product-info.model';
import { IProductPackageItem } from '@core/product/models/product-package-item.model';
import { IProductAllocation, IProductLocation } from '@core/product/models/product-allocation.model';
import { EBookingRestriction } from '@core/product/models/booking-restriction.enum';
import { IProductSession } from '@core/product/models/product-session.model';
import { IDictionary } from '@roller/rdl/models';
import { DateFormatService } from '@core/date/date-format.service';
import { Injectable } from '@angular/core';
import { IProductAvailability } from '@core/product/models/product-availability.model';
import {
  ICXCapacityProduct,
  ICXCapacityProductAvailability,
  ICXCapacityProductPackageOptionSet,
  ICXCapacitySession
} from '@core/product/models/product-capacity.model';
import { CSessionBasedProductTypes, TProductType } from '@core/product/models/product-type.type';
import { indexBy } from '@roller/rdl/utilities/array';
import { rdlGetSessionEndTime } from '@roller/rdl/utilities/session';
import { IProductPackageOptionSet } from './models/product-option-set.model';

/**
 * Stores product availability data in a format that can be used to calculate product capacity
 *
 * !Note - this service is dependent on the following:
 * * parentProducts being merged with the current availabilities (mergeProductsAvailabilities)
 * * The product itself having the parent product Type added after load (addProductTypesToTickets)
 */
@Injectable({
  providedIn: 'root'
})
export class ProductAvailableHelperService {
  // { '2024-10-24': { '12' : {...} }, '2024-10-25': { '12' : {...} } }
  private capacityProductAvailabilityLookup: IDictionary<IDictionary<ICXCapacityProduct>> = {};
  private productAvailabilityLookup: IDictionary<IDictionary<IProduct>> = {};

  constructor(private dateFormatService: DateFormatService) {}

  getCapacityProductAvailabilityLookup(date: Date): IDictionary<ICXCapacityProduct> | undefined {
    const dateHyphenString: string = this.dateFormatService.dateToStringDateOnlyHyphen(date);
    return this.capacityProductAvailabilityLookup[dateHyphenString];
  }

  getProductAvailabilityLookup(date: Date): IDictionary<IProduct> | undefined {
    const dateHyphenString: string = this.dateFormatService.dateToStringDateOnlyHyphen(date);
    return this.productAvailabilityLookup[dateHyphenString];
  }

  /**
   * Whenever product availability data is fetched, store a converted capacity product list as a lookup, accessible by date
   * @param parentProducts - The products with availabilities to map
   * @param date - The date to map the products to, same as availability call 0000-00-00
   */
  storeProductAvailability(parentProducts: IProduct[], date: string) {
    const bookingDate = this.dateFormatService.dateOnlyStringToDate(date);
    const productConversion = parentProducts.flatMap((parent) =>
      parent.products.map((product) => this.mapToCapacityProduct(product, parent, bookingDate))
    );

    this.capacityProductAvailabilityLookup[date] = indexBy(productConversion, (product) => String(product.productId));
    this.productAvailabilityLookup[date] = indexBy(parentProducts, (product) => product.id);
  }

  mapToCapacityOrderItems(items: IOrderItem[]): ICapacityOrderItem[] {
    return items.flatMap((item) => {
      const parentProduct = this.getProductAvailabilityLookup(item.bookingDate)?.[item.parentProductId];
      if (!parentProduct) {
        console.warn(`Parent product ${item.parentProductId} not found`);
        return [];
      }

      return [
        {
          productId: parseInt(item.productId, 10),
          parentProductId: parseInt(item.parentProductId, 10),
          quantity: item.quantity,
          product: this.mapToCapacityProduct(item.product, parentProduct, item.bookingDate),
          packageItems: item.packageItems?.map((packageItem) => this.mapPackageItem(packageItem)),
          bookingDateIndex: this.dateFormatService.toDateIndex(item.bookingDate),
          sessionStart: item.session?.startTime
            ? this.dateFormatService.hourStringToNumber(item.session?.startTime)
            : undefined,
          sessionEnd: item.session?.endTime
            ? this.dateFormatService.hourStringToNumber(item.session?.endTime)
            : undefined,
          additionalGuests: item.additionalGuests
        }
      ];
    });
  }

  mapToCapacitySession(productId: string, session: IProductSession): ICXCapacitySession {
    const allocation = session.allocations.find((allocation) => allocation.productId === productId);
    const startTime = this.dateFormatService.hourStringToNumber(session.startTime);
    const endTime = this.dateFormatService.hourStringToNumber(session.endTime);

    return {
      startTime,
      endTime,
      dateIndex: this.dateFormatService.toDateIndex(session.date),
      remainingSessionCapacity: session.remainingSessionCapacity ?? null,
      remainingResourceCapacity: allocation?.bookableCapacityRemaining ?? null,
      isRestricted: false,
      times: this.getResourceTimes(startTime, endTime, allocation),
      resources: this.getSessionResources(allocation),
      isAvailable: !!allocation,
      onlineSalesOpen: session.onlineSalesOpen
    };
  }

  private mapToCapacityProduct(product: IProductInfo, parent: IProduct, bookingDate: Date): ICXCapacityProduct {
    const isPackage = product.type === 'package';

    return {
      productId: parseInt(product.id, 10),
      parentProductId: parseInt(parent.id, 10),
      requiredGroupSize: product.groupSize,
      packageItems: product.packageItems?.map(this.mapPackageItem),
      packageOptionSets: product.packageOptionSets?.map(this.mapPackageOptionSet),
      resources: product.locations?.map((resource) => this.mapResource(resource)) || null,
      managedQuantity: null,
      sessions: this.getSessions(product, parent),
      availability: this.getProductAvailability(parent, product, bookingDate),
      isPackage,
      type: product.type as TProductType,
      maxPurchase: product.maxPurchase,
      minPurchase: product.minPurchase,
      parentMaxPurchase: parent.maxPurchase
    };
  }

  private getSessions(product: IProductInfo, parent: IProduct): ICXCapacitySession[] | undefined {
    if (CSessionBasedProductTypes.includes(product.type as TProductType)) {
      return parent.sessions?.map((session) => this.mapToCapacitySession(product.id, session)) || [];
    }

    return undefined;
  }

  private getProductAvailability(
    parent: IProduct,
    product: IProductInfo,
    bookingDate: Date
  ): ICXCapacityProductAvailability | undefined {
    const productAvailability = parent.availabilities?.find((avail: IProductAvailability) =>
      this.dateFormatService.isSameDay(avail.date, bookingDate)
    );

    if (!productAvailability) {
      return;
    }

    const allocation = productAvailability.allocations.find((allocation) => allocation.productId === product.id);
    return {
      productId: parseInt(product.id, 10),
      dateIndex: this.dateFormatService.toDateIndex(productAvailability.date),
      remainingResourceCapacity: allocation?.bookableCapacityRemaining ?? null,
      capacityRemaining: allocation?.capacityRemaining ?? null,
      onlineSalesOpen: productAvailability.onlineSalesOpen,
      isAvailable: !!allocation
    };
  }

  private mapPackageItem(packageItem: IProductPackageItem): ICapacityPackageItem {
    return {
      packageProductId: parseInt(packageItem.packageProductId, 10),
      parentProductId: parseInt(packageItem.parentProductId, 10),
      productId: parseInt(packageItem.productId, 10),
      quantity: packageItem.quantity,
      cost: packageItem.cost
    };
  }

  private mapPackageOptionSet(optionSet: IProductPackageOptionSet): ICXCapacityProductPackageOptionSet {
    return {
      packageProductOptionSetId: parseInt(optionSet.productPackageOptionSetId, 10),
      packageProductId: parseInt(optionSet.packageProductId, 10),
      productIds: optionSet.productIds.map((productId) => parseInt(productId, 10)),
      quantity: optionSet.quantity,
      cost: optionSet.cost
    };
  }

  private mapResource(resource: IProductLocation): ICapacityProductResource {
    return { ...resource, bookingRestriction: this.mapBookingRestriction(resource.bookingRestriction) };
  }

  private mapBookingRestriction(restriction: EBookingRestriction): ECapacityBookingRestriction {
    if (restriction === EBookingRestriction.SingleTicketType) {
      return ECapacityBookingRestriction.singleTicketType;
    } else if (restriction === EBookingRestriction.SingleProduct) {
      return ECapacityBookingRestriction.singleProduct;
    }

    return ECapacityBookingRestriction.multipleProducts;
  }

  private getSessionResources(allocation?: IProductAllocation): ICapacityProductResource[] {
    return allocation?.locations.map((resource) => this.mapResource(resource)) || [];
  }

  private getResourceTimes(
    startTime: number,
    endTime: number,
    allocation?: IProductAllocation
  ): ICapacityProductSessionResourceTime[] {
    if (!allocation?.resourceTimes) {
      return [];
    }

    return allocation.resourceTimes?.map((time) => {
      return {
        ...time,
        resourceStartTime: rdlGetSessionEndTime(startTime, time.startMinutes) || startTime,
        resourceEndTime: rdlGetSessionEndTime(startTime, time.endMinutes) || endTime
      };
    });
  }
}
